import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models
// ============================================================

export interface CUSCustomer {
  cus_mid: number;
  cus_code: string;
  comp_type: string;
  cus_cname: string;
  cus_ename: string;
  tax_code: string;
  country_code: string;
  phone: string;
  comp_fax: string;
  web: string;
  comp_mail: string;
  address: string;
  invoice: string;
  invoic_address: string;
  tax_rate: number;
  brand: string;
  pers: number;
  creat_year: string;
  credit_limit: number;
  annual_sales: string;
  capital_amt: string;
  explain: string;
  main_prop: string;
  cus_prop: string;
  trade_terms: string;
  trade_name: string;
  nda: boolean;
  nda_date: string | null;
  ispa: boolean;
  ispa_date: string | null;
  co_development: boolean;
  co_branding: boolean;
  ref_cus: boolean;
  is_active: boolean;
  isvalid: boolean;
  appoint_cus: string;
  attach_files_mid: number;
  c_user: string;
  user_name: string;
  c_date: string | null;
  u_user: string;
  u_date: string | null;
  // Sub-tables
  contacts: CUSCusPers[];
  prd_items: CUSCusPrdItems[];
  prices: CUSCusPrices[];
  color_stand: CUSCusColorStand | null;
  color_paras: CUSCusColorPara[];
  color_quality: CUSCusColorQuality[];
  order_principle: CUSCusOrder | null;
  samples: CUSCusSample[];
}

export interface CUSCusPers {
  pers_did: number;
  cus_mid: number;
  duty: string;
  title: string;
  contact_name: string;
  gender: string;
  contact_mail: string;
  special_line: string;
  extension: string;
  mobile1: string;
  mobile2: string;
}

export interface CUSCusPrdItems {
  items_did: number;
  cus_mid: number;
  cus_type: string;
  clt_sort: string;
  brand_cus: string;
  cls_sale_qty: number;
  curr_code: string;
  annual_sales: number;
  market: string;
  make_country: string;
  unit: string;
  bl_qty: number;
  bl_amount: number;
  pur_country: string;
  explain: string;
}

export interface CUSCusPrices {
  price_did: number;
  cus_mid: number;
  sort: string;
  prd_type: string;
  order_type: string;
  unit: string;
  color_min_qty: number;
  order_min_qty: number;
  color_qty_unit: string;
  raise_price: number;
  raise_unit_price: number;
  accept_qty: number;
  accept_perc: number;
  accept_qty_unit: string;
  ybb_day: number;
  wbb_day: number;
  price_level: string;
  surplus_stock: number;
  normal_cost: number;
  unusual_cost: number;
  profit: number;
  pay_obj: string;
  comm_pay: number;
}

export interface CUSCusColorStand {
  color_stand_did: number;
  cus_mid: number;
  cs_code: string;
  cs_name: string;
  place: string;
  humidity: number;
  diff_humidity: number;
  temp: number;
  diff_temp: number;
  color_standard: string;
  cs_req: string;
  color_table: string;
  color_qty: number;
  copies: number;
  req_size: string;
  conf_pers: string;
  send_obj: string;
  light_box: string;
  observer: string;
  cloth_place: string;
  c_direction: string;
  allow_standard: string;
  cloth_plies: number;
  card_plies: number;
  data_color: string;
  formula: string;
  gloss: string;
  uv_include: boolean;
  pore: string;
  refle: string;
  test_times: number;
  angle: string;
}

export interface CUSCusColorPara {
  color_para_did: number;
  cus_mid: number;
  fabric_sort: string;
  lihgt_order: string;
  lihgt_name: string;
  tube_size: string;
  energy: string;
  s_delta_l: number;
  s_delta_a: number;
  s_delta_b: number;
  sciede: number;
  sdl_isl: number;
  sdc_csc: number;
  sdhsh: number;
  scmcde: number;
}

export interface CUSCusColorQuality {
  color_qual_did: number;
  cus_mid: number;
  ti_code: string;
  ins_code: string;
  allow_level: string;
}

export interface CUSCusOrder {
  order_did: number;
  cus_mid: number;
  out_packing: string;
  roll: string;
  offer_gcb: boolean;
  qty: string;
  send_pers: string;
  affirm_pers: string;
  affirm_packing: boolean;
  format_spec: string;
  offer_ins_rep: boolean;
  ins_stand: string;
  ins_speci: string;
  kd: string;
  affirm_appear: boolean;
  offer_whx_rep: boolean;
  whx_stand: string;
  rep_pro: string;
  surveyor: string;
  affirm_whx: boolean;
  claim_req: string;
}

export interface CUSCusSample {
  sample_did: number;
  cus_mid: number;
  sample_note: string;
}

export interface ListResponse {
  data: CUSCustomer[];
  total: number;
  page: number;
  size: number;
}

export interface ValidationError {
  field: string;
  code: string;
  message: string;
}

// ============================================================
// API Functions
// ============================================================

const BASE = '/api/customer';

const customerApi = {
  list: async (page = 1, size = 20, keyword?: string): Promise<ListResponse> => {
    if (keyword) {
      const res = await apiClient.get(`${BASE}/search`, { params: { keyword, page, size } });
      return res.data;
    }
    const res = await apiClient.get(BASE, { params: { page, size } });
    return res.data;
  },

  search: async (keyword: string, page = 1, size = 20): Promise<ListResponse> => {
    const res = await apiClient.get(`${BASE}/search`, { params: { keyword, page, size } });
    return res.data;
  },

  get: async (id: number): Promise<CUSCustomer> => {
    const res = await apiClient.get(`${BASE}/${id}`);
    return res.data;
  },

  create: async (data: Partial<CUSCustomer>): Promise<CUSCustomer> => {
    const res = await apiClient.post(BASE, data);
    return res.data;
  },

  update: async (id: number, data: Partial<CUSCustomer>): Promise<CUSCustomer> => {
    const res = await apiClient.put(`${BASE}/${id}`, data);
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE}/${id}`);
  },
};

export default customerApi;
